import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { theme } from './styles/theme';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import AuthAction from './components/AuthAction';
import Dashboard from './components/Dashboard';
import Homepage from './components/Homepage';
import Features from './components/Features';
import About from './components/About';
import Contact from './components/Contact';
import Pricing from './components/Pricing';
// import Blog from './components/Blog'; // Hidden for MVP
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';
import Navigation from './components/Navigation';
import Footer from './components/Footer';

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleGetStarted = () => {
    if (user) {
      // User is already logged in, take them to dashboard
      navigate('/dashboard');
    } else {
      // Redirect to signup page
      navigate('/signup');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSizes.xl,
        color: theme.colors.textLight
      }}>
        Loading ByteBot...
      </div>
    );
  }

  const styles = {
    app: {
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.colors.background,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      flex: 1,
    },
  };

  return (
    <div style={styles.app}>
      <Routes>
        {/* Auth Routes - No navigation/footer */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/action" element={<AuthAction />} />
        
        {/* Dashboard Routes - All require authentication */}
        <Route 
          path="/dashboard/*" 
          element={
            user ? (
              <Dashboard user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        
        {/* Marketing Website Routes */}
        <Route 
          path="/*" 
          element={
            <>
              <Navigation onGetStarted={handleGetStarted} />
              <div style={styles.content}>
                <Routes>
                  <Route path="/" element={<Homepage onGetStarted={handleGetStarted} />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/contact" element={<Contact />} />
                  {/* <Route path="/blog" element={<Blog />} /> */} {/* Hidden for MVP */}
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
              <Footer />
            </>
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App; 